import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Btn } from "../UI";
import { FLabel, FInput } from "../../pages/engagement/FormControls";
import { RichTextEditor } from "../RichTextEditor";
import { textOf } from "../../lib/richText";
import { useSaveShortcut, SAVE_HINT } from "../../lib/useSaveShortcut";
import { Plus, X, CheckCircle2, Save } from "lucide-react";

/** Edit a draft handout. Approving locks the wording and makes it ready to print, email, or share. */
export function HandoutEditor({ h, onDone, onCancel }) {
  const [form, setForm] = useState({ title: h.title, body: h.body, links: h.links || [] });
  const [busy, setBusy] = useState(false);
  const setLink = (i, k, v) => setForm((s) => ({ ...s, links: s.links.map((l, j) => (j === i ? { ...l, [k]: v } : l)) }));
  const save = async (approve) => {
    setBusy(true);
    try {
      await api.put(`/client-handouts/${h.id}`, { ...form, links: form.links.filter((l) => l.url.trim()) });
      if (approve) await api.post(`/client-handouts/${h.id}/approve`);
      toast.success(approve ? "Handout approved" : "Draft saved");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  useSaveShortcut(() => { if (!busy) save(false); });
  return (
    <div className="space-y-4" data-testid="edu-editor">
      <div className="rounded-xl bg-[#FFF8E1] px-3 py-2 text-xs text-[#6D4C41]">Review every line before approving. {h.source === "ai" ? "This text was written by AI and may contain mistakes." : "Edit the wording to fit this client."}</div>
      <div><FLabel>Title *</FLabel><FInput data-testid="edu-editor-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
      <div><FLabel>Handout text *</FLabel><RichTextEditor testId="edu-editor-body" value={form.body} onChange={(html) => setForm((s) => ({ ...s, body: html }))} /></div>
      <div>
        <FLabel>Links (https only)</FLabel>
        <div className="space-y-2">
          {form.links.map((l, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`edu-editor-link-${i}`}>
              <FInput value={l.label} placeholder="Label" onChange={(e) => setLink(i, "label", e.target.value)} />
              <FInput value={l.url} placeholder="https://" onChange={(e) => setLink(i, "url", e.target.value)} />
              <button type="button" aria-label="Remove link" onClick={() => setForm((s) => ({ ...s, links: s.links.filter((_, j) => j !== i) }))} className="text-[#C62828]"><X size={14} /></button>
            </div>
          ))}
          <button type="button" data-testid="edu-editor-add-link" onClick={() => setForm((s) => ({ ...s, links: [...s.links, { label: "", url: "" }] }))} className="flex items-center gap-1 text-xs font-semibold text-[#4E342E] hover:underline"><Plus size={12} /> Add link</button>
        </div>
      </div>
      <div className="flex flex-wrap justify-end gap-2 border-t border-[#F1EEE7] pt-4">
        <Btn variant="ghost" onClick={onCancel}>Back</Btn>
        <Btn variant="outline" data-testid="edu-editor-save" disabled={busy} onClick={() => save(false)} title={`Save draft (${SAVE_HINT})`} aria-keyshortcuts="Control+S"><Save size={14} /> Save draft</Btn>
        <Btn data-testid="edu-editor-approve" disabled={busy || !form.title.trim() || textOf(form.body).length < 20} onClick={() => save(true)}><CheckCircle2 size={14} /> Approve</Btn>
      </div>
    </div>
  );
}
